import { useCallback, useEffect, useState } from "react";
import {
  fetchVoices,
  voiceCategory,
  voiceLanguage,
  type VoiceItem,
  type VoicesResponse,
} from "@/api/voices";

const GENDER_OPTIONS = ["", "male", "female", "neutral", "unknown"];
const LANGUAGE_OPTIONS = ["", "en", "ko", "hi", "tr", "es", "fr", "de", "ja", "pt", "pl", "ru", "ar", "zh"];
const ACCENT_OPTIONS = [
  "",
  "american",
  "british",
  "standard",
  "indian",
  "mexican",
  "brazilian",
  "seoul",
  "istanbul",
  "irish",
  "southern",
];
const CATEGORY_OPTIONS = [
  "",
  "professional",
  "narrative_story",
  "conversational",
  "characters_animation",
  "informative_educational",
];
const LIMIT_OPTIONS = [10, 25, 50];

type DraftFilters = {
  search: string;
  gender: string;
  language: string;
  accent: string;
  category: string;
};

type FetchParams = DraftFilters & { limit: number; offset: number };

const initialDraft: DraftFilters = {
  search: "",
  gender: "",
  language: "",
  accent: "",
  category: "",
};

const initialFetch: FetchParams = { ...initialDraft, limit: 25, offset: 0 };

export default function VoicesPage() {
  const [draft, setDraft] = useState<DraftFilters>(initialDraft);
  const [committed, setCommitted] = useState<FetchParams>(initialFetch);

  const [data, setData] = useState<VoicesResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (p: FetchParams) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchVoices({
        search: p.search.trim() || undefined,
        gender: p.gender || undefined,
        language: p.language || undefined,
        accent: p.accent || undefined,
        category: p.category || undefined,
        limit: p.limit,
        offset: p.offset,
      });
      setData(res);
    } catch (e) {
      setData(null);
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(committed);
  }, [committed, load]);

  const applyFilters = (e: React.FormEvent) => {
    e.preventDefault();
    setCommitted((c) => ({ ...draft, limit: c.limit, offset: 0 }));
  };

  const canPrev = committed.offset > 0;
  const canNext = data ? committed.offset + committed.limit < data.total : false;

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-4 md:p-8">
      <div>
        <h1 className="text-xl font-semibold text-[#1C1A17]" style={{ fontFamily: "'Inter', sans-serif" }}>
          Voice catalog
        </h1>
        <p className="mt-1 text-sm text-[#6B6355]" style={{ fontFamily: "'Inter', sans-serif" }}>
          Browse ElevenLabs voices synced to Postgres (GET /api/voices).
        </p>
      </div>

      <form className="space-y-3 rounded-lg border border-[#E0D8CC] bg-white p-4" onSubmit={applyFilters}>
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-[#1C1A17]" htmlFor="voice-search">
            Search
          </label>
          <input
            id="voice-search"
            type="search"
            value={draft.search}
            onChange={(e) => setDraft((d) => ({ ...d, search: e.target.value }))}
            placeholder="Name, description, voice id…"
            className="rounded border border-[#E0D8CC] px-3 py-2 text-sm"
          />
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium" htmlFor="voice-gender">
              Gender
            </label>
            <select
              id="voice-gender"
              value={draft.gender}
              onChange={(e) => setDraft((d) => ({ ...d, gender: e.target.value }))}
              className="rounded border border-[#E0D8CC] px-2 py-2 text-sm"
            >
              <option value="">Any</option>
              {GENDER_OPTIONS.filter(Boolean).map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium" htmlFor="voice-language">
              Language
            </label>
            <select
              id="voice-language"
              value={draft.language}
              onChange={(e) => setDraft((d) => ({ ...d, language: e.target.value }))}
              className="rounded border border-[#E0D8CC] px-2 py-2 text-sm"
            >
              <option value="">Any</option>
              {LANGUAGE_OPTIONS.filter(Boolean).map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium" htmlFor="voice-accent">
              Accent
            </label>
            <select
              id="voice-accent"
              value={draft.accent}
              onChange={(e) => setDraft((d) => ({ ...d, accent: e.target.value }))}
              className="rounded border border-[#E0D8CC] px-2 py-2 text-sm"
            >
              <option value="">Any</option>
              {ACCENT_OPTIONS.filter(Boolean).map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium" htmlFor="voice-category">
              Category
            </label>
            <select
              id="voice-category"
              value={draft.category}
              onChange={(e) => setDraft((d) => ({ ...d, category: e.target.value }))}
              className="rounded border border-[#E0D8CC] px-2 py-2 text-sm"
            >
              <option value="">Any</option>
              {CATEGORY_OPTIONS.filter(Boolean).map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium" htmlFor="voice-limit">
              Page size
            </label>
            <select
              id="voice-limit"
              value={committed.limit}
              onChange={(e) => {
                const n = Number(e.target.value);
                setCommitted((c) => ({ ...c, limit: n, offset: 0 }));
              }}
              className="rounded border border-[#E0D8CC] px-2 py-2 text-sm"
            >
              {LIMIT_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
          <button
            type="submit"
            className="rounded bg-[#2C2416] px-4 py-2 text-sm font-medium text-[#FAF8F4]"
            disabled={loading}
          >
            {loading ? "Loading…" : "Apply filters"}
          </button>
        </div>
      </form>

      {error && (
        <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-900" role="alert">
          {error}
        </div>
      )}

      {data && (
        <div className="text-sm text-[#6B6355]">
          Showing {data.items.length ? committed.offset + 1 : 0}–{committed.offset + data.items.length} of {data.total}{" "}
          (limit {committed.limit}, offset {committed.offset})
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="rounded border border-[#E0D8CC] px-3 py-1.5 text-sm disabled:opacity-40"
          disabled={!canPrev || loading}
          onClick={() => setCommitted((c) => ({ ...c, offset: Math.max(0, c.offset - c.limit) }))}
        >
          Previous page
        </button>
        <button
          type="button"
          className="rounded border border-[#E0D8CC] px-3 py-1.5 text-sm disabled:opacity-40"
          disabled={!canNext || loading}
          onClick={() => setCommitted((c) => ({ ...c, offset: c.offset + c.limit }))}
        >
          Next page
        </button>
      </div>

      <ul className="space-y-4">
        {data?.items.map((v: VoiceItem) => (
          <li key={v.voice_id} className="rounded-lg border border-[#E0D8CC] bg-white p-4">
            <VoiceCard voice={v} />
          </li>
        ))}
      </ul>

      {data && data.items.length === 0 && !loading && (
        <p className="text-sm text-[#6B6355]">No voices match these filters.</p>
      )}
    </div>
  );
}

function VoiceCard({ voice }: { voice: VoiceItem }) {
  const lang = voiceLanguage(voice.labels);
  const cat = voiceCategory(voice.labels);
  const src = voice.preview_url?.trim();

  return (
    <div className="space-y-2">
      <h2 className="text-base font-semibold text-[#1C1A17]" style={{ fontFamily: "'Inter', sans-serif" }}>
        {voice.provider_name || voice.voice_id}
      </h2>
      <dl className="grid gap-1 text-sm text-[#3D3220] sm:grid-cols-2">
        <div>
          <dt className="text-[#6B6355]">Gender</dt>
          <dd>{voice.gender}</dd>
        </div>
        <div>
          <dt className="text-[#6B6355]">Accent</dt>
          <dd>{voice.accent}</dd>
        </div>
        <div>
          <dt className="text-[#6B6355]">Language</dt>
          <dd>{lang || "—"}</dd>
        </div>
        <div>
          <dt className="text-[#6B6355]">Category</dt>
          <dd>{cat || "—"}</dd>
        </div>
        <div>
          <dt className="text-[#6B6355]">Source</dt>
          <dd>{voice.source_scope ?? "—"}</dd>
        </div>
        <div>
          <dt className="text-[#6B6355]">Voice ID</dt>
          <dd className="break-all font-mono text-xs">{voice.voice_id}</dd>
        </div>
      </dl>
      {src ? (
        <audio controls className="mt-2 w-full max-w-md" preload="none">
          <source src={src} />
        </audio>
      ) : (
        <p className="text-sm text-[#6B6355]">No preview URL</p>
      )}
    </div>
  );
}
